import { NOTIFICATIONS_PAGE_SIZE } from "@shared/notifications-data";
import type { FeedResponse, NotificationRow } from "@shared/notifications-data";
import {
  Bell,
  Cake,
  CalendarHeart,
  CreditCard,
  Gift,
  Heart,
  Info,
  LifeBuoy,
  PenLine,
  Send,
  UserPlus,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { api, edenFetch } from "@/lib/eden";
import { queryClient } from "@/lib/queryClient";

/**
 * The stored web notification feed (PRD 07 §5.6 / D8). Rows are written
 * server-side by the reminder dispatch web lane (dispatch-transport.ts) and the
 * card/BR triggers (card-notifications.ts); this module is the client contract
 * for the header bell + panel. The API (server/routes/user/notifications.ts)
 * hand-types its rows, so the row shape here mirrors that response.
 */
// Row/page shapes live in @shared/notifications-data (one source with mobile).
export type { NotificationRow } from "@shared/notifications-data";

/** Feed cache key. Shared with use-websocket.tsx's live-update handler. */
export const NOTIFICATIONS_FEED_KEY = ["/api/user/notifications"] as const;
/** Badge cache key. Shared with use-websocket.tsx's live-update handler. */
export const NOTIFICATIONS_UNREAD_KEY = ["/api/user/notifications/unread-count"] as const;
/** Preferences cache key (the §5.6 two-lane category set). */
export const NOTIFICATION_PREFS_KEY = ["/api/user/notification-preferences"] as const;
/** Reminder cadence cache key (lead_days / local_send_hour). */
export const REMINDER_SETTINGS_KEY = ["/api/user/reminder-settings"] as const;

export { NOTIFICATIONS_PAGE_SIZE } from "@shared/notifications-data";

/**
 * Per-category presentation. Categories are the canonical set stamped on the
 * `notification` row (schema.sql: reminder, card_received, thank_you,
 * signer_added, send_confirmation, sign_invite, billing, system); an unknown
 * category falls back to the bell so a new server category still renders.
 */
interface CategoryMeta {
  icon: LucideIcon;
  /** Circle background + icon color (token utilities). */
  iconWrapClass: string;
}

const DEFAULT_META: CategoryMeta = { icon: Bell, iconWrapClass: "bg-muted text-muted-foreground" };

const CATEGORY_META: Record<string, CategoryMeta> = {
  reminder: { icon: Cake, iconWrapClass: "bg-primary/10 text-primary" },
  card_received: { icon: Gift, iconWrapClass: "bg-primary/10 text-primary" },
  thank_you: { icon: Heart, iconWrapClass: "bg-rose-100 text-rose-500" },
  signer_added: { icon: PenLine, iconWrapClass: "bg-amber-100 text-amber-600" },
  send_confirmation: { icon: Send, iconWrapClass: "bg-sky-100 text-sky-600" },
  sign_invite: { icon: UserPlus, iconWrapClass: "bg-amber-100 text-amber-600" },
  birthday_request: { icon: CalendarHeart, iconWrapClass: "bg-primary/10 text-primary" },
  billing: { icon: CreditCard, iconWrapClass: "bg-slate-100 text-slate-600" },
  support: { icon: LifeBuoy, iconWrapClass: "bg-primary/10 text-primary" },
  system: { icon: Info, iconWrapClass: "bg-slate-100 text-slate-600" },
};

export function categoryMeta(category: string): CategoryMeta {
  return CATEGORY_META[category] ?? DEFAULT_META;
}

const fetchFeedPage = (offset: number) =>
  edenFetch(
    api.api.user.notifications.get({
      query: { limit: NOTIFICATIONS_PAGE_SIZE, offset },
    }),
  ) as Promise<FeedResponse>;

const fetchUnreadCount = () =>
  edenFetch(api.api.user.notifications["unread-count"].get()) as Promise<{ unread_count: number }>;

const postRead = (notificationId?: string) =>
  edenFetch(
    api.api.user.notifications.read.post(notificationId ? { notification_id: notificationId } : {}),
  ) as Promise<{ unread_count: number }>;

/** Mark every unread notification that links to `linkPath` read (a support thread). */
const postReadByLink = (linkPath: string) =>
  edenFetch(api.api.user.notifications.read.post({ link_path: linkPath })) as Promise<{
    unread_count: number;
  }>;

/**
 * One category's two-lane preference (PRD 07 §5.6/§6.5): `is_inbox_enabled`
 * gates the stored web feed, `is_push_enabled` gates web push + Expo. Email
 * gating is deliberately NOT here (email_subscription — the Email Settings
 * page, PRD 02 §6.7 division).
 */
/** One row of the D10a grid: a category with its three lanes (PRD 07 D10a, 2026-09-08). */
export interface PreferenceRow {
  category: string;
  label: string;
  description: string;
  group_code: string;
  group_label: string;
  is_push_enabled: boolean;
  is_inbox_enabled: boolean;
  /** Whether the lane exists for this category at all (marketing has no web feed). */
  is_push_available: boolean;
  is_inbox_available: boolean;
  /** Locked = always on (account, subscription, transactional card mails). */
  is_email_locked: boolean;
  is_email_enabled: boolean;
  /** Some but not all of the category's mails are off (a per-mail unsubscribe link was used). */
  is_email_partial: boolean;
  email_slugs: string[];
}

/** Per-address layer (D10): which of the member's addresses get reminder / marketing mail. */
export interface PreferenceAddress {
  user_email_id: string;
  email: string;
  is_primary: boolean;
  is_verified: boolean;
  is_receiving: boolean;
  reminders_enabled: boolean;
  marketing_enabled: boolean;
}

export interface PreferencesResponse {
  push_available: boolean;
  preferences: PreferenceRow[];
  email_addresses: PreferenceAddress[];
}

const fetchPreferences = () =>
  edenFetch(api.api.user["notification-preferences"].get()) as Promise<PreferencesResponse>;

type PreferencePutBody = Parameters<(typeof api.api.user)["notification-preferences"]["put"]>[0];

export interface PreferencePut {
  category: string;
  is_push_enabled?: boolean;
  is_inbox_enabled?: boolean;
  is_email_enabled?: boolean;
}

const putPreference = (body: PreferencePut) =>
  edenFetch(api.api.user["notification-preferences"].put(body as PreferencePutBody));

export interface AddressPreferencePut {
  user_email_id: string;
  reminders_enabled?: boolean;
  marketing_enabled?: boolean;
}

const putAddressPreference = (body: AddressPreferencePut) =>
  edenFetch(api.api.user["notification-preferences"].address.put(body));

/** Per-member reminder cadence (PRD 02 §7.5): lead days sorted desc server-side. */
export interface ReminderSettings {
  lead_days: number[];
  local_send_hour: number;
  is_email_default: boolean;
  is_push_default: boolean;
}

const fetchReminderSettings = () =>
  edenFetch(api.api.user["reminder-settings"].get()) as Promise<ReminderSettings>;

const putReminderSettings = (body: ReminderSettings) =>
  edenFetch(api.api.user["reminder-settings"].put(body));

export const notificationsApi = {
  fetchFeedPage,
  fetchUnreadCount,
  postRead,
  postReadByLink,
  fetchPreferences,
  putPreference,
  putAddressPreference,
  fetchReminderSettings,
  putReminderSettings,
};

/** Shape react-query's infinite cache carries for the feed. */
export interface FeedInfiniteData {
  pages: FeedResponse[];
  pageParams: number[];
}

/**
 * Apply a live `notification:new` websocket event: set the badge from the
 * event's authoritative count and refetch the feed if it's cached. The event
 * payload is deliberately NOT prepended as a row — the admin reminder
 * test-fire emits this event with an ephemeral id and no stored row
 * (reminder-test-fire.ts), and a hand-built row would also have to fabricate
 * fields the event omits (template_slug, created_at). The stored row is the
 * truth; a refetch reads it.
 */
export function applyLiveNotificationEvent(unreadCount: number): void {
  queryClient.setQueryData(NOTIFICATIONS_UNREAD_KEY, { unread_count: unreadCount });
  void queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_FEED_KEY });
}
